import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  CalendarBlank,
  UserCircle,
  ChartLineUp,
  Note,
  Fire,
  PaperPlaneTilt,
  Users,
  Trophy,
  ArrowUp,
  ArrowDown,
  Minus,
  X,
  CaretLeft,
  CaretRight,
  Megaphone,
  Lock,
  Heart,
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import Banner from "../components/Banner";

// Shared date formatter: "2026-04-17" -> "04/17/26"
export const formatDateUS = (iso) => {
  if (!iso) return "";
  const parts = iso.split("-");
  if (parts.length !== 3) return iso;
  const [y, m, d] = parts;
  return `${m}/${d}/${y.slice(2)}`;
};
import { Input } from "../components/ui/input";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ==============================
// 1. DAILY HISTORY CALENDAR
// ==============================
export const DailyHistory = ({ onSelectDate }) => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/daily-history/${year}/${month}`).then(r => setData(r.data)).catch(e => console.error("Failed to fetch history:", e));
  }, [year, month]);

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(year - 1); }
    else setMonth(month - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear(year + 1); }
    else setMonth(month + 1);
  };

  const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const daysInMonth = new Date(year, month, 0).getDate();
  const firstDayOfWeek = new Date(year, month - 1, 1).getDay(); // 0=Sun

  const days = data?.days || {};
  const notes = data?.notes || {};

  return (
    <div className="dashboard-card" data-testid="daily-history">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <CalendarBlank weight="bold" className="w-5 h-5 text-slate-600" />
          <h3 className="text-lg font-bold" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>Daily History</h3>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={prevMonth} className="p-1 hover:bg-slate-100 rounded"><CaretLeft className="w-5 h-5" /></button>
          <span className="font-semibold text-sm min-w-[100px] text-center">{monthNames[month-1]} {year}</span>
          <button onClick={nextMonth} className="p-1 hover:bg-slate-100 rounded"><CaretRight className="w-5 h-5" /></button>
        </div>
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1 text-center">
        {["S","M","T","W","T","F","S"].map((d,i) => (
          <div key={`day-header-${i}`} className="text-xs font-semibold text-slate-400 py-1">{d}</div>
        ))}
        {Array.from({ length: firstDayOfWeek }).map((_, i) => <div key={`empty-${i}`} />)}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const dateStr = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
          const dayData = days[dateStr];
          const hasNote = notes[dateStr];
          const todayStr = new Date().toLocaleDateString("en-CA", { timeZone: "America/Denver" });
          const isToday = dateStr === todayStr;
          const isFuture = dateStr > todayStr;

          return (
            <button
              key={day}
              onClick={() => !isFuture && onSelectDate(dateStr)}
              disabled={isFuture}
              className={`relative p-1 rounded-md text-sm transition-colors ${
                isToday ? "ring-2 ring-blue-400" : ""
              } ${
                isFuture
                  ? "opacity-30 cursor-not-allowed"
                  : dayData
                    ? dayData.at_goal
                      ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
                      : "bg-amber-50 text-amber-800 hover:bg-amber-100"
                    : "hover:bg-slate-100"
              }`}
              data-testid={`history-day-${dateStr}`}
            >
              <div className="font-medium">{day}</div>
              {dayData && (
                <div className="text-[10px] leading-tight">{(dayData.capture_rate * 100).toFixed(0)}%</div>
              )}
              {hasNote && <div className="absolute top-0 right-0 w-1.5 h-1.5 bg-blue-500 rounded-full" />}
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-emerald-100 rounded" /> At Goal</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-amber-50 border border-amber-200 rounded" /> Below Goal</span>
        <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 bg-blue-500 rounded-full" /> Has Note</span>
      </div>
    </div>
  );
};

// ==============================
// 2. CREW REPORT CARD
// ==============================
export const CrewReportCard = ({ crewId, onClose }) => {
  const [report, setReport] = useState(null);

  useEffect(() => {
    if (crewId) {
      axios.get(`${API}/crew-report/${crewId}`).then(r => setReport(r.data)).catch(e => console.error("Failed to fetch report:", e));
    }
  }, [crewId]);

  if (!report) return null;

  const trendIcon = report.trend === "improving"
    ? <ArrowUp weight="bold" className="w-4 h-4 text-emerald-500" />
    : report.trend === "declining"
    ? <ArrowDown weight="bold" className="w-4 h-4 text-red-500" />
    : <Minus weight="bold" className="w-4 h-4 text-slate-400" />;

  // Compose a role line from the flags returned by the report endpoint.
  // Falls back to "Wow Crew" so the header never reads blank.
  const roleLine = (() => {
    if (report.is_admin) return "Admin";
    if (report.is_district_manager) return "District Manager";
    if (report.position) return report.position;
    if (report.is_manager) return "Manager";
    return "Wow Crew";
  })();

  return (
    <Dialog open={!!crewId} onOpenChange={() => onClose()}>
      <DialogContent
        className="max-w-md p-0 gap-0 overflow-hidden border-0 shadow-2xl [&>button]:hidden"
        data-testid="crew-report-card"
      >
        {/* Header — profile photo over a blue gradient band. Matches the
            visual language of the founder card. (User ask 2026-05-25:
            "report cards should show profile pictures right now they
            don't can we make those better too?") */}
        <div className="relative">
          <div
            className="h-20 w-full"
            style={{
              background:
                "linear-gradient(135deg, var(--fb-blue, #0048B3) 0%, #3B82F6 60%, #60A5FA 100%)",
            }}
          />
          {/* Single circular close button on the gradient. */}
          <button
            type="button"
            onClick={() => onClose()}
            className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center text-white transition-colors z-10"
            aria-label="Close"
            data-testid="report-card-close"
          >
            <X weight="bold" className="w-3.5 h-3.5" />
          </button>
          <div className="absolute left-1/2 -translate-x-1/2 top-10">
            {report.photo_url ? (
              <img
                src={report.photo_url}
                alt={report.first_name}
                className="w-20 h-20 rounded-full object-cover object-top ring-4 ring-white shadow-lg bg-white"
                data-testid="report-card-photo"
              />
            ) : (
              <div
                className="w-20 h-20 rounded-full ring-4 ring-white shadow-lg flex items-center justify-center text-white font-black"
                style={{ backgroundColor: "var(--fb-blue)", fontFamily: "'Five Below Gothic', Chivo, sans-serif", fontSize: 30 }}
                data-testid="report-card-photo-fallback"
              >
                {(report.first_name || "?").charAt(0).toUpperCase()}
              </div>
            )}
          </div>
        </div>

        <div className="px-5 pt-12 pb-5 max-h-[80vh] overflow-y-auto">
          <div className="text-center mb-3">
            <h2
              className="text-2xl font-black text-slate-900 leading-tight"
              style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif", letterSpacing: "-0.01em" }}
              data-testid="report-card-name"
            >
              {report.first_name}
            </h2>
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mt-0.5">
              {roleLine}
            </p>
            {report.fun_motto && (
              <p className="text-xs italic text-slate-600 mt-2 px-4 leading-snug" data-testid="report-card-motto">
                <span className="text-blue-400">“</span>{report.fun_motto}<span className="text-blue-400">”</span>
              </p>
            )}
          </div>

          <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-slate-50 rounded-md p-3 text-center">
              <p className="text-2xl font-black" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>{report.total_emails}</p>
              <p className="text-xs text-slate-500">Total Emails</p>
            </div>
            <div className="bg-slate-50 rounded-md p-3 text-center">
              <p className="text-2xl font-black" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>{report.avg_per_day.toFixed(1)}</p>
              <p className="text-xs text-slate-500">Avg/Day</p>
            </div>
            <div className="bg-slate-50 rounded-md p-3 text-center">
              <p className="text-2xl font-black" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>{report.days_worked}</p>
              <p className="text-xs text-slate-500">Days Worked</p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="bg-amber-50 rounded-md p-3 text-center">
              <p className="text-xl font-bold text-amber-700">{report.best_count}</p>
              <p className="text-xs text-amber-600">Best Day</p>
            </div>
            <div className="rounded-md p-3 text-center" style={{ backgroundColor: "#DBEAFE" }}>
              <p className="text-xl font-bold" style={{ color: "var(--fb-blue)" }}>{report.streak}</p>
              <p className="text-xs" style={{ color: "var(--fb-blue)" }}>Day Streak</p>
            </div>
            <div className="bg-slate-50 rounded-md p-3 text-center flex flex-col items-center">
              <div className="flex items-center gap-1">{trendIcon}<span className="text-sm font-bold">{report.trend}</span></div>
              <p className="text-xs text-slate-500">{report.recent_avg.toFixed(1)} vs {report.older_avg.toFixed(1)}</p>
            </div>
          </div>

          {/* Heart Balloon contribution removed 2026-05-19 with the campaign cleanup. */}

          {report.standings && (
            <div data-testid="report-standings">
              <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2">
                Your Store · Top 10
              </p>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { key: "lw", label: "LW", sub: "Last Week" },
                  { key: "wtd", label: "WTD", sub: "Week to Date" },
                  { key: "mtd", label: "MTD", sub: "Month to Date" },
                ].map(({ key, label, sub }) => {
                  const s = report.standings[key] || {};
                  const inTop = s.in_top_10;
                  return (
                    <div
                      key={key}
                      className={`rounded-lg p-3 text-center border transition-colors ${
                        inTop
                          ? "bg-gradient-to-br from-amber-50 to-white border-amber-200"
                          : "bg-slate-50 border-slate-200"
                      }`}
                      data-testid={`standing-${key}`}
                    >
                      <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">{label}</p>
                      {inTop ? (
                        <p
                          className="text-2xl font-black leading-none mt-1"
                          style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif", color: "#B45309" }}
                        >
                          #{s.rank}
                        </p>
                      ) : (
                        <p className="text-sm font-bold text-slate-400 mt-1">—</p>
                      )}
                      <p className="text-[10px] text-slate-500 mt-0.5">
                        {s.emails || 0} {s.emails === 1 ? "email" : "emails"}
                      </p>
                      <p className="text-[9px] text-slate-400 uppercase tracking-wider mt-0.5">{sub}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* District-wide standings — same windows but ranked against EVERY
              active crew in the district (200+ peers). Gives the crew member
              a sense of where they sit in the wider pack. Headline shows the
              monthly rank since that's the most decisive cycle for prizes.
              (User ask 2026-02: "can we add their standings in the district".) */}
          {report.district_standings && report.district_standings.total_crew > 0 && (
            <div data-testid="report-district-standings">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs uppercase tracking-wider font-semibold text-violet-700">
                  District · {report.district_standings.total_crew} crew
                </p>
                {report.district_standings.store_rank?.rank && (
                  <p className="text-[10px] font-bold text-slate-500" data-testid="report-store-district-rank">
                    Store ranks{" "}
                    <span className="text-violet-700 font-black">#{report.district_standings.store_rank.rank}</span>
                    {" of "}
                    {report.district_standings.store_rank.total_stores}
                    {" "}MTD
                  </p>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { key: "lw", label: "LW", sub: "Last Week" },
                  { key: "wtd", label: "WTD", sub: "Week to Date" },
                  { key: "mtd", label: "MTD", sub: "Month to Date" },
                ].map(({ key, label, sub }) => {
                  const s = report.district_standings[key] || {};
                  const hasRank = !!s.rank;
                  return (
                    <div
                      key={key}
                      className={`rounded-lg p-3 text-center border transition-colors ${
                        hasRank && s.percentile >= 75
                          ? "bg-gradient-to-br from-violet-50 to-white border-violet-200"
                          : "bg-slate-50 border-slate-200"
                      }`}
                      data-testid={`district-standing-${key}`}
                    >
                      <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">{label}</p>
                      {hasRank ? (
                        <>
                          <p
                            className="text-2xl font-black leading-none mt-1"
                            style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif", color: "#6D28D9" }}
                          >
                            #{s.rank}
                          </p>
                          <p className="text-[10px] text-slate-500 mt-0.5">
                            of {s.total_ranked}
                            {s.percentile != null && (
                              <span className="ml-1 font-bold text-violet-700">· top {100 - s.percentile + 1}%</span>
                            )}
                          </p>
                        </>
                      ) : (
                        <p className="text-sm font-bold text-slate-400 mt-1">—</p>
                      )}
                      <p className="text-[9px] text-slate-400 uppercase tracking-wider mt-0.5">{sub}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ==============================
// 3. WEEKLY SUMMARY
// ==============================
export const WeeklySummary = () => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/weekly-summary`).then(r => setData(r.data)).catch(e => console.error("Failed to fetch weekly:", e));
  }, []);

  if (!data || !data.top_3.length) return null;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="dashboard-card w-full text-left hover:border-red-200 transition-colors"
        data-testid="weekly-summary-button"
      >
        <div className="flex items-center gap-2 mb-2">
          <Trophy weight="bold" className="w-5 h-5 text-amber-500" />
          <h3 className="text-lg font-bold" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>Last Week Recap</h3>
          <span className="text-xs text-slate-400 ml-auto">{formatDateUS(data.week_start)} to {formatDateUS(data.week_end)}</span>
        </div>
        <div className="flex items-center gap-6">
          <div>
            <span className="text-2xl font-black" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>{(data.capture_rate * 100).toFixed(1)}%</span>
            <span className={`ml-2 text-sm font-bold ${data.at_goal ? "text-emerald-600" : "text-amber-600"}`}>
              {data.at_goal ? "Goal Hit" : "Below Goal"}
            </span>
          </div>
          <div className="text-sm text-slate-500">
            {data.days_at_goal}/{data.days_tracked} days at goal
          </div>
          <div className="text-sm">
            Top: <span className="font-bold">{data.top_3[0]?.first_name}</span>
          </div>
        </div>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="weekly-summary-dialog">
          <DialogHeader>
            <DialogTitle>Weekly Recap: {formatDateUS(data.week_start)} to {formatDateUS(data.week_end)}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-slate-50 rounded-md p-3 text-center">
                <p className="text-xl font-bold">{data.total_transactions}</p>
                <p className="text-xs text-slate-500">Transactions</p>
              </div>
              <div className="bg-slate-50 rounded-md p-3 text-center">
                <p className="text-xl font-bold">{data.total_emails}</p>
                <p className="text-xs text-slate-500">Emails</p>
              </div>
              <div className="bg-slate-50 rounded-md p-3 text-center">
                <p className="text-xl font-bold">{(data.capture_rate * 100).toFixed(1)}%</p>
                <p className="text-xs text-slate-500">Rate</p>
              </div>
            </div>

            <div>
              <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2">Top Performers</p>
              {data.top_3.map((c, i) => (
                <div key={c.crew_id} className="flex items-center justify-between py-1">
                  <span className="flex items-center gap-2">
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                      ["bg-amber-400 text-white", "bg-slate-300", "bg-amber-700 text-white"][i] || "bg-slate-100"
                    }`}>{i+1}</span>
                    {c.first_name}
                  </span>
                  <span className="font-bold">{c.total}</span>
                </div>
              ))}
            </div>

            {data.most_improved && (
              <div className="bg-emerald-50 rounded-md p-3">
                <p className="text-xs uppercase tracking-wider font-semibold text-emerald-600 mb-1">Most Improved</p>
                <p className="font-bold">{data.most_improved.first_name}: {data.most_improved.this_week} emails (+{data.most_improved.improvement} from last week)</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// ==============================
// 4. DAY NOTES
// ==============================
export const DayNotes = ({ date }) => {
  const [note, setNote] = useState("");
  const [saved, setSaved] = useState("");
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    axios.get(`${API}/day-notes/${date}`).then(r => {
      setNote(r.data.note || "");
      setSaved(r.data.note || "");
    }).catch(e => console.error("Failed to fetch note:", e));
  }, [date]);

  const handleSave = async () => {
    try {
      await axios.post(`${API}/day-notes`, { date, note });
      setSaved(note);
      setEditing(false);
      toast.success("Note saved");
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="dashboard-card" data-testid="day-notes">
      <div className="flex items-center gap-2 mb-2">
        <Note weight="bold" className="w-5 h-5 text-slate-600" />
        <h3 className="text-sm font-bold" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>Day Note</h3>
      </div>

      {editing ? (
        <div className="flex gap-2">
          <Input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Short staffed, holiday traffic..."
            className="flex-1 text-sm"
            autoFocus
            data-testid="day-note-input"
          />
          <Button size="sm" onClick={handleSave} className="fb-btn-primary" data-testid="save-note-button">Save</Button>
          <Button size="sm" variant="ghost" onClick={() => { setNote(saved); setEditing(false); }}>Cancel</Button>
        </div>
      ) : (
        <button
          onClick={() => setEditing(true)}
          className="text-sm text-left w-full hover:bg-slate-50 rounded p-1 -m-1 transition-colors"
          data-testid="edit-note-button"
        >
          {saved ? (
            <span className="text-slate-700">{saved}</span>
          ) : (
            <span className="text-slate-400 italic">Tap to add a note (e.g. short staffed, power outage...)</span>
          )}
        </button>
      )}
    </div>
  );
};

// ==============================
// 5. GOAL STREAK
// ==============================
export const GoalStreak = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/goal-streak`).then(r => setData(r.data)).catch(e => console.error("Failed to fetch streak:", e));
  }, []);

  if (!data) return null;

  const onStreak = data.current_streak > 0;
  return (
    <div
      className={`relative rounded-3xl px-4 py-3.5 flex items-center gap-4 shadow-md ring-1 overflow-hidden ${
        onStreak
          ? "bg-gradient-to-r from-orange-50 via-amber-50 to-pink-50 ring-orange-200"
          : "bg-white ring-slate-200"
      }`}
      data-testid="goal-streak"
    >
      <span
        className={`inline-flex items-center justify-center w-12 h-12 rounded-2xl shadow-md shrink-0 ${
          onStreak ? "text-white" : "bg-slate-100 text-slate-400"
        }`}
        style={onStreak ? { background: "linear-gradient(135deg, #f97316, #ec4899)" } : {}}
      >
        <Fire weight="fill" className="w-6 h-6" />
      </span>
      <div className="min-w-0">
        <div className="flex items-baseline gap-2.5 flex-wrap">
          <span
            className={`text-3xl font-black tabular-nums leading-none ${onStreak ? "text-orange-600" : "text-slate-400"}`}
            style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}
          >
            {data.current_streak}
          </span>
          <span className={`text-sm font-bold ${onStreak ? "text-orange-800" : "text-slate-600"}`}>
            day streak at {(data.goal_rate * 100).toFixed(0)}% goal
          </span>
        </div>
        <p className="text-[11px] font-semibold text-slate-500 mt-1">
          Longest streak: <span className="font-black text-slate-700">{data.longest_streak}</span> days
        </p>
      </div>
    </div>
  );
};

// ==============================
// 6. MANAGER MESSAGING
// ==============================
export const ManagerMessaging = ({ user, crew }) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [target, setTarget] = useState("team"); // "team" or crew_id
  const [sending, setSending] = useState(false);
  const [recentMessages, setRecentMessages] = useState([]);

  const activeCrew = crew.filter(c => c.active);

  useEffect(() => {
    axios.get(`${API}/messages?limit=5`).then(r => setRecentMessages(r.data)).catch(e => console.error("Failed to fetch messages:", e));
  }, [open]);

  const handleSend = async () => {
    if (!message.trim()) return;
    setSending(true);
    try {
      const isManagersOnly = target === "managers";
      const targetCrew = (target !== "team" && target !== "managers") ? activeCrew.find(c => c.id === target) : null;
      await axios.post(`${API}/messages`, {
        from_id: user?.crew_id || "",
        from_name: user?.first_name || "Manager",
        to_id: (target === "team" || target === "managers") ? "" : target,
        to_name: targetCrew?.first_name || "",
        message: message.trim(),
        managers_only: isManagersOnly,
      });
      const label = isManagersOnly ? "Managers only message sent!" : (target === "team" ? "Team message sent!" : `Message sent to ${targetCrew?.first_name}`);
      toast.success(label);
      setMessage("");
      const r = await axios.get(`${API}/messages?limit=5`);
      setRecentMessages(r.data);
    } catch (e) { toast.error("Failed to send"); }
    finally { setSending(false); }
  };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        className="fb-btn-primary"
        data-testid="open-messaging-button"
      >
        <PaperPlaneTilt weight="bold" className="w-4 h-4 mr-2" />
        Message Crew
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="messaging-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PaperPlaneTilt weight="bold" className="w-5 h-5" style={{ color: "var(--fb-blue)" }} />
              Send Message
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            <div>
              <label className="block text-sm font-medium mb-1">To</label>
              <Select value={target} onValueChange={setTarget}>
                <SelectTrigger data-testid="message-target-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="team">Everyone (Team Blast)</SelectItem>
                  <SelectItem value="managers">Managers Only</SelectItem>
                  {activeCrew.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.first_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Message</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={target === "team" ? "Great energy today team! Let's push for goal!" : "You're doing amazing today!"}
                className="w-full border border-neutral-200 rounded-md p-3 text-sm resize-none h-20 focus:outline-none focus:ring-2 focus:ring-blue-400"
                data-testid="message-input"
              />
            </div>

            <Button
              onClick={handleSend}
              disabled={!message.trim() || sending}
              className="w-full fb-btn-primary"
              data-testid="send-message-button"
            >
              {sending ? "Sending..." : target === "team" ? "Send to Team" : target === "managers" ? "Send to Managers" : "Send Message"}
            </Button>

            {recentMessages.length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2">Recent Messages</p>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {recentMessages.map(m => (
                    <div key={m.id} className="bg-neutral-50 rounded-md p-2 text-sm flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <span className="font-medium">{m.from_name}</span>
                        {m.managers_only ? (
                          <span className="text-amber-600 text-xs ml-1 font-semibold">Managers Only</span>
                        ) : m.is_team_blast ? (
                          <span className="text-blue-600 text-xs ml-1">to Team</span>
                        ) : (
                          <span className="text-neutral-400 text-xs ml-1">to {m.to_name}</span>
                        )}
                        <p className="text-neutral-600 mt-0.5">{m.message}</p>
                      </div>
                      <button
                        onClick={async () => {
                          try {
                            await axios.delete(`${API}/messages/${m.id}`);
                            setRecentMessages(prev => prev.filter(msg => msg.id !== m.id));
                            toast.success("Message unsent");
                          } catch (e) {
                            toast.error("Failed to unsend");
                          }
                        }}
                        className="text-xs text-red-400 hover:text-red-600 font-medium flex-shrink-0 px-1.5 py-0.5 rounded hover:bg-red-50 transition-colors"
                        data-testid={`unsend-${m.id}`}
                      >
                        Unsend
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// ==============================
// CREW MESSAGE BANNER (for crew tally page)
// ==============================
export const CrewMessageBanner = ({ crewId, isManager = false }) => {
  const [messages, setMessages] = useState([]);
  const [dismissed, setDismissed] = useState(new Set());
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const r = await axios.get(`${API}/messages?crew_id=${crewId}&is_manager=${isManager}&limit=5`);
        // Home banner is BANNER-channel messages only (2026-06-15 user fix:
        // "blasts are for like banner messages on their home page, whole
        // team messages got to their inbox shared thread"). The Whole Team
        // mailbox thread continues to receive ALL team posts including
        // banners — banners just additionally light up here. Legacy
        // messages that pre-date the channel field don't have it set, so
        // they show on banner only if explicitly `is_team_blast` AND
        // tagged banner — i.e. they stop showing on banner after this
        // change, which is the intended soft transition.
        setMessages(
          r.data.filter(
            (m) =>
              !m.read_by?.includes(crewId) &&
              !m.deleted_at &&
              m.delivery_channel === "banner",
          ),
        );
      } catch (e) {
        console.error("Failed to fetch messages:", e);
      }
    };
    fetchMessages();
    const interval = setInterval(fetchMessages, 5000);
    return () => clearInterval(interval);
  }, [crewId, isManager]);

  const handleDismiss = async (msgId) => {
    setDismissed(prev => new Set([...prev, msgId]));
    try {
      await axios.put(`${API}/messages/${msgId}/read?crew_id=${crewId}`);
    } catch (e) {
      console.error("Failed to mark message read:", e);
    }
  };

  // Sender-only retract: soft-deletes the blast on the backend (sets
  // `deleted_at` so every recipient sees the "Message deleted" tombstone in
  // their Inbox thread). 30-second window to keep the affordance frictionless
  // for typo recovery without letting managers silently rewrite history.
  // (user request 2026-05-03 "add a small 'Marked sent — tap to undo'
  // affordance on the manager's own blast banner".)
  const handleUndoSend = async (msgId) => {
    setDismissed((prev) => new Set([...prev, msgId]));
    try {
      await axios.delete(`${API}/messages/${msgId}?crew_id=${crewId}`);
      toast.success("Blast unsent — the team won't see it.");
    } catch (e) {
      console.error("Failed to undo blast:", e);
      toast.error("Couldn't undo — try the Inbox thread instead.");
      // Roll back the optimistic dismiss so the banner reappears and the user
      // can retry. Fetching messages on the next 5s tick will reconcile.
      setDismissed((prev) => {
        const next = new Set(prev);
        next.delete(msgId);
        return next;
      });
    }
  };

  // 1Hz tick driving the live "Undo (Ns)" countdown. We tick the clock here
  // (instead of inside each banner) so the banner stays a pure render of
  // props. The window expires gracefully — the trailing slot disappears at
  // 0s without any state change beyond the existing `now` advance.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const UNDO_WINDOW_MS = 30_000;

  const visible = messages.filter(m => !dismissed.has(m.id));
  if (visible.length === 0) return null;

  // Cap the home page to the newest blast. If multiple are unread, show a
  // subtle "+N more" chip so the rest stay discoverable without dominating
  // the home screen (which is fixed-height on mobile). User taps the chip
  // to expand the full stack. Prevents the "banners pushed the tiles off
  // the screen" bug reported 2026-04-20.
  const [firstBlast, ...restBlasts] = visible;
  const extraCount = restBlasts.length;
  const renderable = expanded ? visible : [firstBlast];

  return (
    <div className="space-y-2 mb-4" data-testid="crew-messages">
      {renderable.map((m) => {
        const variant = m.managers_only ? "managers" : m.is_team_blast ? "announcement" : "success";
        const Icon = m.managers_only ? Lock : Megaphone;
        // Clamp long blasts to 3 lines so a novel-length announcement doesn't
        // shove the tile grid off the screen. Full text still lives in the
        // Inbox thread; this is the at-a-glance summary.
        const body = (m.message || "").trim();
        const isLong = body.length > 140 || body.split(/\n/).length > 3;
        // Sender-only undo window. We compute the remaining seconds from
        // `created_at` (ISO8601 UTC) — if the field is missing or unparseable,
        // we treat the window as already closed so we never expose Undo on
        // ancient messages by accident.
        const isSender = m.from_id === crewId;
        const createdMs = m.created_at ? Date.parse(m.created_at) : NaN;
        const ageMs = Number.isFinite(createdMs) ? now - createdMs : Number.POSITIVE_INFINITY;
        const undoMsLeft = isSender ? Math.max(0, UNDO_WINDOW_MS - ageMs) : 0;
        const canUndo = isSender && undoMsLeft > 0;
        const undoSecLeft = Math.ceil(undoMsLeft / 1000);
        return (
          <Banner
            key={m.id}
            variant={variant}
            icon={Icon}
            title={`${m.from_name} ${isSender ? (canUndo ? "→ Sent · just now" : "→ Sent") : m.managers_only ? "→ Managers" : m.is_team_blast ? "→ Team" : "→ You"}`}
            onDismiss={canUndo ? null : () => handleDismiss(m.id)}
            trailing={canUndo ? (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); handleUndoSend(m.id); }}
                className="text-[11px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full bg-white text-blue-700 ring-1 ring-blue-300 hover:bg-blue-50 active:scale-95 transition-all tabular-nums whitespace-nowrap"
                data-testid={`banner-undo-${m.id}`}
                aria-label={`Undo blast — ${undoSecLeft} seconds left`}
              >
                Undo · {undoSecLeft}s
              </button>
            ) : null}
            testid={`banner-crew-${m.id}`}
          >
            <div
              className="whitespace-pre-wrap"
              style={{
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {body}
            </div>
            {isLong && (
              <p className="text-[11px] font-semibold text-slate-500 mt-1">
                Open the Inbox to read the full message.
              </p>
            )}
          </Banner>
        );
      })}
      {extraCount > 0 && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="text-xs font-bold tracking-wide text-blue-700 hover:text-blue-900 transition-colors px-2 py-1 rounded hover:bg-blue-50"
          data-testid="banner-more-toggle"
        >
          {expanded ? "Collapse" : `+${extraCount} more announcement${extraCount === 1 ? "" : "s"}`}
        </button>
      )}
    </div>
  );
};
